import { PayeeRequirement } from "@wingspanhq/wingspan-api-sdk";
import { EmployeeTaxFormsMetadata } from "./configs/helpers/getEmployeeTaxFormsProgress";
import { useMemo } from "react";
import {
  RequirementTableCallback,
  RequirementTableItemData,
  RequirementTableRowConfig
} from "../../types";
import { buildConfigFields } from "../../utils";
import { REQUIREMENT_TYPE_EMPLOYEE_TAX_FORMS } from "./constants";
import { usePayeeEmployeeTaxFormsConfig } from "./configs/payee/usePayeeEmployeeTaxFormsConfig";
import { usePayerEmployeeTaxFormsConfig } from "./configs/payer/usePayerEmployeeTaxFormsConfig";

// The data source is the engagement itself — progress is embedded on the
// requirement (dataSourceMetadata), so unlike Signature/SharedFile there is
// no per-requirement document fetch (no context queries, never loading).
// `?? {}` keeps buildConfigFields from rendering its error cell for a
// requirement read before its first data-source refresh.
const getEmployeeTaxFormsAdditionalData = (
  requirement: PayeeRequirement
): NonNullable<EmployeeTaxFormsMetadata> =>
  requirement.dataSourceMetadata ?? {};

export const useEmployeeTaxFormsRequirementTableConfig = (
  callback: RequirementTableCallback,
  _requirements: RequirementTableItemData[]
): RequirementTableRowConfig[typeof REQUIREMENT_TYPE_EMPLOYEE_TAX_FORMS] => {
  const payerEmployeeTaxFormsConfig = usePayerEmployeeTaxFormsConfig(callback);
  const payeeEmployeeTaxFormsConfig = usePayeeEmployeeTaxFormsConfig(callback);

  const fields = useMemo(
    () =>
      // `any` additional data mirrors Registration: the metadata bag is not a
      // member of the closed RequirementAdditionalData union.
      buildConfigFields<any, typeof REQUIREMENT_TYPE_EMPLOYEE_TAX_FORMS>({
        payerConfig: payerEmployeeTaxFormsConfig,
        payeeConfig: payeeEmployeeTaxFormsConfig,
        getAdditionalData: getEmployeeTaxFormsAdditionalData,
        // Progress lives on the requirement's dataSourceMetadata, not a separate document.
        hasDataSource: false
      })(),
    [payerEmployeeTaxFormsConfig, payeeEmployeeTaxFormsConfig]
  );

  return {
    ...fields,
    getAdditionalData: () => undefined,
    isLoading: false,
    dataUpdatedAt: ""
  };
};
